/**
 * Brand Hub SEO / Social identity for Not Broken Forever homepage.
 * Sets document title, description, canonical, Open Graph, and hreflang.
 * Safe for SPA: upserts tags; cleans up on unmount when leave hub routes.
 */

const SITE = "https://notbrokenforever.com";

const COPY = {
  he: {
    title: "Not Broken Forever | הספר, InnerVoice ו־Orasis",
    description:
      "Not Broken Forever: מרחב להבנה, ריפוי ובהירות. שלושה שערים. הספר, מסע יומי עם InnerVoice, ודוחות עומק של Orasis.",
  },
  en: {
    title: "Not Broken Forever | Book, InnerVoice, and Orasis",
    description:
      "Not Broken Forever: a home for understanding, healing, and clarity. Three gates. The book, a daily InnerVoice journey, and Orasis depth reports.",
  },
  es: {
    title: "Not Broken Forever | Libro, InnerVoice y Orasis",
    description:
      "Not Broken Forever: un espacio para comprender, sanar y encontrar claridad. Tres caminos: el libro, un viaje diario con InnerVoice y los informes de profundidad de Orasis.",
  },
};

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.property || attrs.name ? "meta" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null) return;
    el.setAttribute(k, v);
  });
  return el;
}

function upsertLink(rel, attrs) {
  const selector =
    rel === "alternate" && attrs.hreflang
      ? `link[rel="alternate"][hreflang="${attrs.hreflang}"]`
      : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null) return;
    el.setAttribute(k, v);
  });
  return el;
}

function bookOgImage(lang) {
  const code = COPY[lang] ? lang : "en";
  return `${SITE}/books/book-${code === "he" ? "he" : code === "es" ? "es" : "en"}.png`;
}

/**
 * @param {{ lang: string, pathBase?: string }} opts
 * pathBase: "/" or "/unified-homepage-staging"
 * @returns {() => void} cleanup
 */
export function applyBrandHubSeo({ lang, pathBase = "/" }) {
  const code = COPY[lang] ? lang : "en";
  const copy = COPY[code];
  const base = pathBase.endsWith("/") && pathBase.length > 1 ? pathBase.slice(0, -1) : pathBase;
  const pageUrl = `${SITE}${base === "/" ? "/" : base}?lang=${code}`;
  const image = bookOgImage(code);

  const previousTitle = document.title;
  document.title = copy.title;
  document.documentElement.lang = code === "he" ? "he" : code === "es" ? "es" : "en";

  upsertMeta('meta[name="description"]', { name: "description", content: copy.description });

  upsertLink("canonical", { href: pageUrl });

  upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Not Broken Forever" });
  upsertMeta('meta[property="og:title"]', { property: "og:title", content: copy.title });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: copy.description });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  upsertMeta('meta[property="og:locale"]', {
    property: "og:locale",
    content: code === "he" ? "he_IL" : code === "es" ? "es_ES" : "en_US",
  });

  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: copy.title });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: copy.description });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

  const langs = ["he", "en", "es"];
  langs.forEach((l) => {
    upsertLink("alternate", {
      hreflang: l,
      href: `${SITE}${base === "/" ? "/" : base}?lang=${l}`,
    });
  });
  upsertLink("alternate", {
    hreflang: "x-default",
    href: `${SITE}${base === "/" ? "/" : base}?lang=en`,
  });

  return () => {
    document.title = previousTitle;
  };
}

export const BRAND_HUB_SEO_COPY = COPY;
